import React, {createRef, PureComponent, RefObject} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Dropdown, DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SearchDropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdownHeader';
import {emojiToShortcode} from '../../../../../srcCommon/helpers/emoji/emojiHelpers';
import {EmojiDefinitionAndSkinTone} from '../../../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {EmojiCategoriesEnum} from '../../../../services/emojis/emojiList';
import {EmojiDefinition} from '../../../../services/emojis/emojis';
import {SkinTonesEnum} from '../../../../services/emojis/emojiTypes';
import {EmojiSearcher} from '../../../../store/stateless/emojiSelectors';
import {EmojiDropdownFooter} from './emojiDropdownFooter';
import {EmojiDropdownList} from './emojiDropdownList';
import {EmojiDropdownToolbar} from './emojiDropdownToolbar';

/*
 * Props.
 */

export type EmojiSelectHandler = (shortcode: string, definition: EmojiDefinitionAndSkinTone) => void;

interface EmojiDropdownProps extends DropdownProps {
  skinTone: SkinTonesEnum;
  frequentlyUsedEmoji: ReadonlyArray<EmojiDefinition>;
  emojiList: ReadonlyArray<EmojiDefinition>;
  emojiSearcher: EmojiSearcher;
  onEmojiSelect?: EmojiSelectHandler;
  updateSkinTone: (arg: SkinTonesEnum) => void;
  recordEmojiUse: (definition: EmojiDefinition) => void;
  enableCustomEmoji: boolean;
  showCustomEmojiCategory: boolean;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

/*
 * Component.
 */

interface EmojiPickerDropdownState {
  activeCategory: EmojiCategoriesEnum;
  previewEmoji?: EmojiDefinition;
  searchQuery: string;
}

export class EmojiDropdown extends PureComponent<EmojiDropdownProps, EmojiPickerDropdownState> {
  constructor(props: EmojiDropdownProps) {
    super(props);

    // First the default category to consider "active".
    const hasFrequentlyUsed = props.frequentlyUsedEmoji.length > 0;
    const activeCategory = hasFrequentlyUsed
      ? EmojiCategoriesEnum.FREQUENTLY_USED
      : EmojiCategoriesEnum.SMILEYS_AND_PEOPLE;

    // Initialize the state.
    this.state = {
      activeCategory,
      searchQuery: '',
    };
    this.listRef = createRef<EmojiDropdownList>();
  }

  private readonly listRef: RefObject<EmojiDropdownList>;

  /*
   * Event handlers.
   */

  private readonly onSearchInput: (arg: string) => void = (value) => {
    this.setState({searchQuery: value});
  };

  /*
   * Toolbar event handlers.
   */

  private readonly onCategorySelect: (arg: EmojiCategoriesEnum) => void = (category) => {
    // We need a ref to the list to do anything.
    const currentList = this.listRef.current;
    if (!currentList) {
      return;
    }

    // Make sure we're not in search before scrolling.
    this.setState({searchQuery: ''}, () => {
      // Scroll to the specified category.
      currentList.scrollTo(category);
    });
  };

  private readonly onSkinToneSelect: (arg: SkinTonesEnum) => void = (skinTone) => {
    const {updateSkinTone} = this.props;
    return updateSkinTone && updateSkinTone(skinTone);
  };

  /*
   * List event handlers.
   */

  private readonly onEmojiMouseEnter: (definition: EmojiDefinition) => void = (emoji) => {
    this.setState({previewEmoji: emoji});
  };

  private readonly onEmojiMouseLeave: () => void = () => {
    this.setState({previewEmoji: undefined});
  };

  private readonly onEmojiClick: (definition: EmojiDefinition) => void = (emoji) => {
    // Find the shortcode for this emoji.
    const {skinTone, onEmojiSelect, recordEmojiUse} = this.props;
    const shortcode = emojiToShortcode(emoji, skinTone);

    // Keep track of usage.
    recordEmojiUse(emoji);

    // Notify our parent.
    return onEmojiSelect && onEmojiSelect(shortcode, {emoji, skinTone});
  };

  private readonly onListCategoryChange: (arg: EmojiCategoriesEnum) => void = (category) => {
    this.setState({activeCategory: category});
  };

  /*
   * Render.
   */

  render() {
    const {skinTone, frequentlyUsedEmoji, enableCustomEmoji, showCustomEmojiCategory, emojiList} = this.props;
    const {activeCategory, previewEmoji, searchQuery} = this.state;
    const selectedCategory = searchQuery ? undefined : activeCategory;

    return (
      <Dropdown {...this.props} width={364}>
        <SearchDropdownHeader
          title={<Trans id="dropdown-emoji-title">Emoji</Trans>}
          value={searchQuery}
          onChange={this.onSearchInput}
        />
        <EmojiDropdownToolbar
          activeCategory={selectedCategory}
          onCategorySelect={this.onCategorySelect}
          skinTone={skinTone}
          onSkinToneSelect={this.onSkinToneSelect}
          showRecentCategory={frequentlyUsedEmoji.length > 0}
          showCustomEmojiCategory={showCustomEmojiCategory && enableCustomEmoji}
        />
        <StyledContentDiv>
          <EmojiDropdownList
            ref={this.listRef}
            skinTone={skinTone}
            searchQuery={searchQuery}
            onEmojiMouseEnter={this.onEmojiMouseEnter}
            onEmojiMouseLeave={this.onEmojiMouseLeave}
            onEmojiClick={this.onEmojiClick}
            onCategoryChange={this.onListCategoryChange}
            frequentlyUsedEmoji={this.props.frequentlyUsedEmoji}
            emojiSearcher={this.props.emojiSearcher}
            emojiList={emojiList}
            includeCustomEmoji={this.props.enableCustomEmoji}
          />
        </StyledContentDiv>
        <EmojiDropdownFooter
          showAddEmojiButton={this.props.enableCustomEmoji}
          emoji={previewEmoji}
          skinTone={skinTone}
        />
      </Dropdown>
    );
  }
}
